import PromiseWorker from "promise-worker";
import makerjs, {ICombineOptions, IModel, IPoint} from "makerjs";
import { optimize } from 'svgo/dist/svgo.browser';
import parse from 'parse-svg-path';

import map from "lodash/map";
import isEmpty from "lodash/isEmpty";
import every from "lodash/every";
import some from "lodash/some";

import Length from "../../../source/js/shared/models/Geometry/Length";
import isUndefined from "lodash/isUndefined";
import {Helper} from "dxf-mod";

/*
 * Loads a web worker with the help of the Webpack worker-loader.
 *
 * With Webpack5+ this can be converted to `new Worker(new URL("@/webworker", import.meta.url))`
 *
 * @see https://github.com/webpack/webpack/issues/6472
 */
async function loadWorker() { // eslint-disable-line @typescript-eslint/explicit-function-return-type
	let Worker;

	// We only want to use the 'dirty' inline version when using the dev server
	if ( process.env.NODE_ENV === 'production' ) { // eslint-disable-line no-undef
		Worker = await require( 'worker-loader!@/webworker' ); // eslint-disable-line no-undef
	} else {
		Worker = await require( 'worker-loader?inline=fallback!@/webworker' ); // eslint-disable-line no-undef
	}

	return Worker.default;
}

let worker = null;

/**
 * Returns a cached worker instance.
 */
async function getWorker(): Promise<PromiseWorker> {
	if ( worker === null ) {
		const Worker = await loadWorker();

		worker = new PromiseWorker( new Worker() );
	}

	return worker;
}

export async function combineUnionCharacters( model ) { // eslint-disable-line @typescript-eslint/explicit-function-return-type
	const worker = await getWorker();

	return await worker.postMessage( {
		type: 'combine-union-characters',
		model,
	} );
}

export function getContainingModelExceedances( model: IModel, containingModel: IModel ): IModel[] {

	// We need to create this object to ensure we get references to the deleted paths.
	const combineOptions: ICombineOptions = {};

	makerjs.model.combine( containingModel, model, true, true, true, false, combineOptions );

	if ( combineOptions.out_deleted && ! isEmpty( combineOptions.out_deleted[ 1 ].paths ) ) {
		return combineOptions.out_deleted;
	}

	return [];
}

/**
 * Extracts the polygons from the model.
 *
 * @returns {object[]} The extracted polygons.
 */
function extractPolygons( model: IModel ): object[] {
	if ( ! model.paths ) {
		return [ { x: 0, y: 0 } ];
	}

	const mapped = map( model.paths, ( path ) => {
		if ( ! path.end ) {
			return { x: 0, y: 0 };
		}

		return { x: path.end[ 0 ], y: path.end[ 1 ] };
	} );

	// Manually add a point that is halfway the arc. Only applies to half-circles.
	if ( model.type && model.type === 'half-circle' ) {
		mapped.push( { x: model.paths.arc.origin[ 0 ], y: model.paths.arc[ 'radius' ] } );
	}

	return mapped;
}

/**
 * Calculates the area based on the extracted polygons.
 *
 * @returns {number} The area.
 */
function calculateArea( polygons: object[] ): number {
	let area = 0, i, j, point1, point2;

	for( i = 0, j = polygons.length - 1; i < polygons.length; j = i, i+= 1 ) {
		point1 = polygons[ i ];
		point2 = polygons[ j ];

		area += point1.x * point2.y;
		area -= point1.y * point2.x;
	}

	area /= 2;

	return area;
}

/**
 * Calculates the centroid point.
 *
 * @returns {Length[]} The X and Y-coordinates of the centroid.
 */
export function centroid( model: IModel ): Length[] {
	const polygons = extractPolygons( model );

	let x = 0, y = 0, i, j, area, point1, point2;

	for ( i = 0, j = polygons.length - 1; i < polygons.length; j = i, i += 1 ) {
		point1 = polygons[ i ];
		point2 = polygons[ j ];

		area = point1.x * point2.y - point2.x * point1.y;
		x += ( point1.x + point2.x ) * area;
		y += ( point1.y + point2.y ) * area;
	}

	area = calculateArea( polygons ) * 6;

	return [ Length.from_cm( x / area ), Length.from_cm( y / area ) ];
}

/**
 * Fillets the passed model.
 *
 * @param {IModel} model The model whose submodels will be filleted.
 * @param {Length} fillet The fillet to be added.
 *
 * @returns {IModel} The resulting model.
 */
export function filletModel( model: IModel, fillet: Length, reverse = false ): IModel {

	const filletToUse = reverse ? { left: 0, right: ( fillet.in_mm() ) } : { left: ( fillet.in_mm() ), right: 0 };
	const chainsRaw = makerjs.model.findChains( model as IModel, { contain: { alternateDirection: true }, pointMatchingDistance: fillet.in_cm() } );
	const chains: any[] = Array.isArray(chainsRaw) ? chainsRaw : [];
	if (chains.length === 0) {
		return model;
	}

	for(let i = 0; i < chains.length; i++) {
		const chain = chains[i];
		if(chain.links && chain.links.length > 1) {
			const filletModel = makerjs.chain.fillet(chain, filletToUse );

			if(!isEmpty(filletModel)) {
				makerjs.model.addModel(model, filletModel, 'fillets'+i);
			}
		}

		if(chain.contains && chain.contains.length > 0) {
			for(let j = 0; j < chain.contains.length; j++) {
				const childChain = chain.contains[j];
				if(childChain.links && childChain.links.length > 1) {
					const filletModel = makerjs.chain.fillet(childChain, filletToUse );

					if(!isEmpty(filletModel)) {
						makerjs.model.addModel(model, filletModel, 'fillets' + i + '.' + j);
					}
				}
			}
		}
	}

	return model;
}

export function generateArrow( length: number, scale: number ): IModel {
	const hs = 4 * scale;
	const paths = {};

	paths[ 'right-up' ] = new makerjs.paths.Line( [ length, 0 ], [ length-hs, hs ] );
	paths[ 'right-down' ] = new makerjs.paths.Line( [ length, 0 ], [ length-hs, -hs ] );

	paths[ 'main' ] = new makerjs.paths.Line( [ 0, 0 ], [ length, 0 ] );

	paths[ 'left-up' ] = new makerjs.paths.Line( [ 0, 0 ], [ hs, -hs] );
	paths[ 'left-down' ] = new makerjs.paths.Line( [ 0, 0 ], [ hs, hs ] );

	return { models: { arrow: { paths } } };
}

export function generateExtensionLine( length: number ): IModel {
	const ms = length / 2;
	const paths = {};

	paths[ 'left' ] = new makerjs.paths.Line( [ 0, -ms ], [ 0, 2 * ms ] );
	paths[ 'right' ] = new makerjs.paths.Line( [ length, -ms ], [ length, 2 * ms ] );

	paths[ 'main' ] = new makerjs.paths.Line( [ 0, -ms ], [ length, -ms ] );

	return { models: { extension_line: { paths } } };
}

/**
 * Checks whether the passed model completely falls inside the parent model by checking the first model's points.
 *
 * @param {IModel} model The model to check.
 * @param {IModel} parentModel The parent model to check against.
 * @param {boolean} allowPartial Whether to count partial overlapping to be considered valid.
 *
 * @returns {boolean} Whether the first model completely falls inside the second model.
 */
export function isModelInsideParent( model: IModel, parentModel: IModel, allowPartial = false ): boolean {
	const route = [ "models", "modelMidPoint" ];
	let result = true;

	const results = [];

	const callback = allowPartial ? some : every;

	const options = {
		onPath: ( path ): void => {
			let points = makerjs.path.toKeyPoints( path.pathContext );

			points = points.map( ( point ) => {
				return makerjs.point.add( point, path.offset );
			} );

			result = callback( points, ( p ) => makerjs.measure.isPointInsideModel( p, parentModel ) );
			results.push( result );
		}
	};

	makerjs.model.walk( model, options );

	return callback( results );
}

// Chain a model to create one path
export function chainModel(model: IModel): IModel {
	let completeModel = {
		models: {
			shapeModel: {
				models: {},
			},
			cutoutModel: {
				models: {},
			},
		}
	} as IModel;
	const cutoutModel = {} as IModel;

	// Get model keys
	const keys = Object.keys(model.models);

	const chains = makerjs.model.findChains(model, {contain: true});
	if(Array.isArray(chains) && chains.length > 0) {
		chains.forEach((chain, index) => {
			const chainModel = makerjs.chain.toNewModel(chain, true);
			chainModel[ 'originalKey' ] = keys[index] ?? null;
			makerjs.model.addModel(completeModel.models.shapeModel, chainModel, `baseModel`);

			if(Array.isArray(chain.contains) && chain.contains.length > 0) {
				chain.contains.forEach((childChain, childIndex) => {
					const chainModel = makerjs.chain.toNewModel(childChain, true);
					makerjs.model.addModel(cutoutModel, chainModel, `cutoutModel-${index}-${childIndex}`);
				});
			}
		});

		makerjs.model.addModel(completeModel.models.cutoutModel, cutoutModel, `baseModel`);
	} else {

		// Fallback to the original model because the chain failed
		completeModel = model;
	}

	return completeModel;
}

/**
 * Converts a html string to an HTML Element.
 * @param {string} html
 */
export default function htmlToElement (html: string): ChildNode {
	const template = document.createElement('template');

	html = html.trim();
	template.innerHTML = html;

	return template.content.firstChild;
}

function maybeFixCirclePath (path: string): string {
	const commands = parse(path);
	const newPath = commands.flat().join(' ');

	return newPath;
}

export function DXFToModel(dxf: string): IModel {
	let model: IModel = {
		models: {}
	};

	const helper = new Helper(dxf);
	const polylinesObject = helper.toPolylines();

	// @ts-expect-error - library has incorrect typing, return object instead of array of polylines
	polylinesObject.polylines.forEach((polyline, index) => {
		const polylinesText = polyline.vertices.map((vertex) => vertex.join(' ')).join(', ');
		const childModel = makerjs.model.mirror(new makerjs.models.ConnectTheDots(false, polylinesText), false, true);
		makerjs.model.addModel(model, childModel, `polyline-${index}`);
	});

	model = makerjs.model.mirror(model, false, true);

	makerjs.model.zero(model);
	makerjs.model.originate(model);

	return model;
}

export function SVGToModel(svg: string): IModel {
	const svg_optimized = optimize(svg, {});
	const svg_element = htmlToElement(svg_optimized.data) as SVGSVGElement;

	const model: IModel = {
		paths: {}
	};

	for (const element of svg_element.children) {
		const paths = [];
		if (element.tagName === 'path') {
			paths.push(element.getAttribute('d'));
		} else if (element.tagName === 'g') {
			for (const subPath of element.children) {
				if (subPath.tagName === 'path') {
					paths.push(subPath.getAttribute('d'));
				}
			}
		}
		if (paths.length === 0) {
			continue;
		}

		for (const path of paths) {
			const fixedPath = maybeFixCirclePath(path);

			const svgPathData = makerjs.importer.fromSVGPathData(fixedPath);

			makerjs.model.addModel(model, svgPathData, 'line');
		}
	}

	makerjs.model.zero(model);
	makerjs.model.originate(model);

	return model;
}

export function getShapeModelCenter(model: IModel): IPoint {
	const measure = makerjs.measure.modelExtents(model);

	if ( isUndefined( measure ) ) {
		return [0, 0];
	}

	const irregularShapes = [
		'arrow',
		'half-circle',
		'isosceles-triangle',
		'quarter-circle',
		'right-angled-triangle',
		'right-angled-triangle-mirrored',
		'sloped-rectangle',
		'sloped-rectangle-flip-horizontal',
		'sloped-right-rectangle',
		'sloped-right-rectangle-flip-vertical',
	];

	if(!irregularShapes.includes(model.type)) {
		return [measure.center[0], measure.center[1]];
	}

	const center = centroid(model);

	return [center[0].in_cm(), center[1].in_cm()];
}

export async function loadFont(url: string): Promise<object> {
	const { default: openType } = await import( /* webpackChunkName: "OpenType" */ 'opentype.js' );

	let font = {};

	try {

		// Docs claim this approach should work (and it seems to be the case). However, TS suggests this isn't possible.
		font = await openType.load( url );
	} catch( err ) {

		// eslint-disable-next-line no-console
		console.log(err.toString());
	}

	return font;
}
